import React, { createContext, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
	Address,
	AddressSearchFormValues,
	AddressType,
	AddressWithoutId,
	ConnectedDeviceSummary,
	DeviceReading,
	DeviceSummary,
} from '@types';

export interface ModalContextState {
	selectedDevice: DeviceSummary | ConnectedDeviceSummary | undefined;
	setSelectedDevice: (device: DeviceSummary | ConnectedDeviceSummary | undefined) => void;
	selectedDeviceReading: DeviceReading | undefined;
	setSelectedDeviceReading: (reading: DeviceReading | undefined) => void;
	showReportIssueForm: boolean;
	setShowReportIssueForm: (value: boolean) => void;
	showDeviceFaultyFormModal: boolean;
	setShowDeviceFaultyFormModal: (value: boolean) => void;
	showReadingModal: boolean;
	setShowReadingModal: (value: boolean) => void;
	showAddressModal: boolean;
	setShowAddressModal: (value: boolean) => void;
	addressTypeModal: AddressType | undefined;
	setAddressTypeModal: (value: AddressType | undefined) => void;
	streetAddress: Address | AddressWithoutId | undefined;
	setStreetAddress: (address: Address | AddressWithoutId | undefined) => void;
	postalAddress: Address | AddressWithoutId | undefined;
	setPostalAddress: (address: Address | AddressWithoutId | undefined) => void;
	addressFormValuesOnSave: AddressSearchFormValues | undefined;
	setAddressFormValuesOnSave: (values: AddressSearchFormValues | undefined) => void;
	showEmailAddressModal: boolean;
	setShowEmailAddressModal: (value: boolean) => void;
	emailAddress: string | undefined;
	setEmailAddress: (emailAddress: string | undefined) => void;
	emailAddressFromModal: string | undefined;
	setEmailAddressFromModal: (emailAddress: string | undefined) => void;
	showRemoveSensorModal: boolean;
	setShowRemoveSensorModal: (value: boolean) => void;
	showRemoveSensorModalV9: boolean;
	setShowRemoveSensorModalV9: (value: boolean) => void;
	selectedSensorId: string | undefined;
	setSelectedSensorId: (id: string | undefined) => void;
	selectedSensorIdOnRemove: string | undefined;
	setSelectedSensorIdOnRemove: (id: string | undefined) => void;
}

const contextDefaultValues: ModalContextState = {
	selectedDevice: undefined,
	setSelectedDevice: () => {
		// do nothing
	},
	selectedDeviceReading: undefined,
	setSelectedDeviceReading: () => {
		// do nothing
	},
	showReportIssueForm: false,
	setShowReportIssueForm: () => {
		// do nothing
	},
	showDeviceFaultyFormModal: false,
	setShowDeviceFaultyFormModal: () => {
		// do nothing
	},
	showReadingModal: false,
	setShowReadingModal: () => {
		// do nothing
	},
	showAddressModal: false,
	setShowAddressModal: () => {
		// do nothing
	},
	addressTypeModal: undefined,
	setAddressTypeModal: () => {
		// do nothing
	},
	streetAddress: undefined,
	setStreetAddress: () => {
		// do nothing
	},
	postalAddress: undefined,
	setPostalAddress: () => {
		// do nothing
	},
	addressFormValuesOnSave: undefined,
	setAddressFormValuesOnSave: () => {
		// do nothing
	},
	showEmailAddressModal: false,
	setShowEmailAddressModal: () => {
		// do nothing
	},
	emailAddress: undefined,
	setEmailAddress: () => {
		// do nothing
	},
	emailAddressFromModal: undefined,
	setEmailAddressFromModal: () => {
		// do nothing
	},
	showRemoveSensorModal: false,
	setShowRemoveSensorModal: () => {
		// do nothing
	},
	showRemoveSensorModalV9: false,
	setShowRemoveSensorModalV9: () => {
		// do nothing
	},
	selectedSensorId: undefined,
	setSelectedSensorId: () => {
		// do nothing
	},
	selectedSensorIdOnRemove: undefined,
	setSelectedSensorIdOnRemove: () => {
		// do nothing
	},
};

export const ModalContext = createContext<ModalContextState>(contextDefaultValues);

export const ModalContextProvider = ({ children }: { children: React.ReactNode }) => {
	const pathname = usePathname();

	const [selectedDevice, setSelectedDevice] = useState<
		DeviceSummary | ConnectedDeviceSummary | undefined
	>(contextDefaultValues.selectedDevice);
	const [selectedDeviceReading, setSelectedDeviceReading] = useState<DeviceReading | undefined>(
		contextDefaultValues.selectedDeviceReading,
	);
	const [showReportIssueForm, setShowReportIssueForm] = useState<boolean>(
		contextDefaultValues.showReportIssueForm,
	);
	const [showDeviceFaultyFormModal, setShowDeviceFaultyFormModal] = useState<boolean>(
		contextDefaultValues.showDeviceFaultyFormModal,
	);
	const [showReadingModal, setShowReadingModal] = useState<boolean>(
		contextDefaultValues.showReadingModal,
	);
	const [showAddressModal, setShowAddressModal] = useState<boolean>(
		contextDefaultValues.showAddressModal,
	);
	const [addressTypeModal, setAddressTypeModal] = useState<AddressType | undefined>(
		contextDefaultValues.addressTypeModal,
	);
	const [streetAddress, setStreetAddress] = useState<Address | AddressWithoutId | undefined>(
		contextDefaultValues.streetAddress,
	);
	const [postalAddress, setPostalAddress] = useState<Address | AddressWithoutId | undefined>(
		contextDefaultValues.postalAddress,
	);
	const [addressFormValuesOnSave, setOnSaveAddressModal] = useState<
		AddressSearchFormValues | undefined
	>(contextDefaultValues.addressFormValuesOnSave);

	const [showEmailAddressModal, setShowEmailAddressModal] = useState<boolean>(
		contextDefaultValues.showEmailAddressModal,
	);

	const [emailAddress, setEmailAddress] = useState<string | undefined>(
		contextDefaultValues.emailAddress,
	);

	const [emailAddressFromModal, setEmailAddressFromModal] = useState<string | undefined>(
		contextDefaultValues.emailAddressFromModal,
	);

	const [showRemoveSensorModal, setShowRemoveSensorModal] = useState<boolean>(
		contextDefaultValues.showRemoveSensorModal,
	);

	const [showRemoveSensorModalV9, setShowRemoveSensorModalV9] = useState<boolean>(
		contextDefaultValues.showRemoveSensorModalV9,
	);

	const [selectedSensorId, setSelectedSensorId] = useState(contextDefaultValues.selectedSensorId);

	const [selectedSensorIdOnRemove, setSelectedSensorIdOnRemove] = useState(
		contextDefaultValues.selectedSensorIdOnRemove,
	);

	const cleanSelectedData = () => {
		setSelectedDevice(undefined);
		setSelectedDeviceReading(undefined);
	};

	useEffect(() => {
		if (!showReadingModal) {
			cleanSelectedData();
		}
	}, [showReadingModal]);

	/**
	 * Close the report issue side drawer when the page changes
	 */
	useEffect(() => {
		if (showReportIssueForm) {
			setShowReportIssueForm(false);
		}
	}, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<ModalContext.Provider
			value={{
				selectedDevice: selectedDevice,
				setSelectedDevice: setSelectedDevice,
				selectedDeviceReading: selectedDeviceReading,
				setSelectedDeviceReading: setSelectedDeviceReading,
				showReportIssueForm,
				setShowReportIssueForm,
				showDeviceFaultyFormModal,
				setShowDeviceFaultyFormModal,
				showReadingModal,
				setShowReadingModal,
				showAddressModal,
				setShowAddressModal,
				addressTypeModal,
				setAddressTypeModal,
				streetAddress,
				setStreetAddress,
				postalAddress,
				setPostalAddress,
				addressFormValuesOnSave,
				setAddressFormValuesOnSave: setOnSaveAddressModal,
				emailAddress,
				setEmailAddress,
				emailAddressFromModal,
				setEmailAddressFromModal,
				showEmailAddressModal,
				setShowEmailAddressModal,
				showRemoveSensorModal,
				setShowRemoveSensorModal,
				showRemoveSensorModalV9,
				setShowRemoveSensorModalV9,
				selectedSensorId,
				setSelectedSensorId,
				selectedSensorIdOnRemove,
				setSelectedSensorIdOnRemove,
			}}
		>
			{children}
		</ModalContext.Provider>
	);
};
